/**
 * WebSocket клиент для управления браузером через голосового агента
 * Агент может отправлять команды управления браузером через sessionId
 *
 * ВАЖНО: SessionId управляется через sessionManager.js, который обеспечивает
 * единый sessionId с TTL 24 часа для всего приложения.
 */

import { commandExecutor } from './commandExecutor.js';
import { getCurrentPageContext, resolveWebsitePage } from '../utils/pageContextParser.js';
import { config } from '../config/runtimeConfig.js';
import { getOrCreateVisitorId } from './sessionManager.js';
import { carTitle } from '../utils/carTitle';

class BrowserControlWebSocket {
  constructor() {
    this.ws = null;
    // In-flight connect() promise — shared by concurrent callers so a second connect()
    // during the CONNECTING window reuses the same socket instead of opening (and
    // overwriting this.ws with) a second one. See connect() for the race this prevents.
    this._connectPromise = null;
    this.sessionId = null;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isIntentionallyClosed = false;
    this.messageHandlers = new Map();
    // #1485: counts the sockets opened so far. A request sent on an earlier socket lost its
    // stream when that socket closed; useTextChat compares this on 'registered'.
    this.socketGeneration = 0;

    // Константы для localStorage (только для server URL, sessionId управляется sessionManager)
    this.STORAGE_KEY_SERVER_URL = 'browserControl_serverUrl';

    // Конфигурация приложения (routes и metadata)
    // Параметризованные команды генерируются динамически в commandGenerator.js
    this.appConfig = {
      routes: [],
      metadata: {}
    };
  }

  /**
   * Подключение к WebSocket серверу
   * @param {string} url - URL WebSocket сервера
   * @param {string} sessionId - Уникальный ID сессии
   */
  connect(url, sessionId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return Promise.resolve();
    }

    // Race-safe: a connect is already in flight (socket CONNECTING). Reuse the SAME
    // promise instead of opening a second socket. Two concurrent connect() calls — e.g.
    // the #944 WS pre-warm and the navigation-restore reconnect, both firing on mount —
    // would otherwise each create a WebSocket; the second overwrites this.ws, so the
    // first call's awaiter checks isConnected() on the wrong (still-CONNECTING) socket,
    // bails ("WebSocket not connected after restore connect"), and the post-reload
    // tool_cycle_continue is never sent.
    if (this._connectPromise) {
      return this._connectPromise;
    }

    this.sessionId = sessionId;
    this.serverUrl = url;
    this.isIntentionallyClosed = false;

    // Сохраняем данные подключения в localStorage для восстановления
    this.saveToLocalStorage();

    // Проверяем frontend token для авторизации в b2b-api
    const frontendToken = config.FRONTEND_TOKEN;

    // Debug: log token status (use warn for production visibility)
    console.warn('[BrowserControl] Frontend token status:', {
      hasToken: !!frontendToken,
      tokenLength: frontendToken ? frontendToken.length : 0,
      runtimeConfig: typeof window !== 'undefined' && window.runtimeConfig ? 'exists' : 'missing'
    });

    // IMPORTANT: Without frontend token, server will reject connection
    // Don't attempt connection - fail fast with clear error
    if (!frontendToken) {
      console.error('[BrowserControl] ERROR: Frontend token is required for WebSocket connection');
      return Promise.reject(new Error('Frontend token is not configured. Cannot connect to WebSocket.'));
    }

    // Add token to WebSocket URL
    const separator = url.includes('?') ? '&' : '?';
    const wsUrl = `${url}${separator}token=${frontendToken}`;
    console.warn('[BrowserControl] Using frontend token for authentication');

    this._connectPromise = new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected to browser control server');
          this.reconnectAttempts = 0;
          this.socketGeneration += 1;

          // Генерируем карту доступных действий
          const actionsMap = this.getActionsMap();

          // Diagnostic logging before send
          console.log('[BrowserControl] Sending register with actionsMap:', {
            sessionId: this.sessionId,
            hasActionsMap: !!actionsMap,
            commandsCount: actionsMap?.commands?.length ?? 0,
            routesCount: actionsMap?.routes?.length ?? 0,
            currentPage: actionsMap?.currentPage?.route,
          });

          // Регистрируем sessionId на сервере с картой действий
          this.send({
            type: 'register',
            sessionId: this.sessionId,
            actionsMap,
            timestamp: Date.now()
          });

          console.log('[BrowserControl] Registered with actions map:', actionsMap);

          // Re-sync actionsMap после того как React hooks успеют сгенерировать команды
          // Покрывает race condition: register отправляется до useCommands
          setTimeout(() => {
            if (this.isConnected() && this.commands && this.commands.length > 0) {
              console.log('[BrowserControl] Post-register re-sync: updating actionsMap with', this.commands.length, 'commands');
              this.updateActionsMap();
            }
          }, 2000);

          this._connectPromise = null;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this._connectPromise = null;
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          this._connectPromise = null;

          // Пытаемся переподключиться если закрытие не было намеренным
          if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

            setTimeout(() => {
              this.connect(url, this.sessionId);
            }, this.reconnectDelay * this.reconnectAttempts);
          }
        };
      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        this._connectPromise = null;
        reject(error);
      }
    });

    return this._connectPromise;
  }

  /**
   * Обработка входящих сообщений от сервера
   * @param {Object} data - Данные сообщения
   */
  handleMessage(data) {
    console.log('[BrowserControl] Received message from server:', data);

    // Issue #497: Log proactive stream chunks for debugging
    if (data.type === 'proactive-stream-chunk') {
      console.log('[BrowserControl][Issue #497] WS_MESSAGE_RECEIVED:', {
        type: data.type,
        chunkType: data.chunkType,
        sessionId: data.sessionId,
        runId: data.runId,
        contentLength: data.content?.length || 0,
        timestamp: Date.now(),
      });
    }

    const { type, command, params, payload, commandHash } = data;

    if (command === 'execute') {
      console.log('[BrowserControl] Execute command received - payload:', payload, 'params:', params, 'commandHash:', commandHash);
    }

    // Вызываем зарегистрированные обработчики для данного типа
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type);
      handlers.forEach(handler => handler(data));
    }

    // Обработка команд управления браузером
    switch (command) {
      case 'navigate':
        this.handleNavigate(params, commandHash);
        break;
      case 'click':
        this.handleClick(params, commandHash);
        break;
      case 'fill':
        this.handleFill(params, commandHash);
        break;
      case 'scroll':
        this.handleScroll(params, commandHash);
        break;
      case 'execute': {
        // Для execute команд используем payload, который содержит { commandId, params }
        const executePayload = payload || params || {};
        this.handleExecute(executePayload, commandHash);
        break;
      }
      default:
        console.log('Unknown command:', command);
    }
  }

  /**
   * Навигация по внутренним путям приложения (только клиентский роутинг)
   * ВАЖНО: Работает ТОЛЬКО с локальными route, никаких внешних URL!
   * Использует HashRouter для SPA навигации БЕЗ перезагрузки страницы.
   * Полные URL прервут голосовой звонок!
   *
   * @param {Object} params - Параметры навигации
   * @param {string} params.route - Клиентский маршрут (основной параметр)
   * @param {string} params.path - Алиас для route (для обратной совместимости)
   * @param {string} params.url - Устаревший параметр (для обратной совместимости)
   * @param {Object} params.queryParams - Query параметры
   */
  handleNavigate({ route, path, url, queryParams }) {
    // Приоритет: route > path > url (для обратной совместимости)
    const targetPath = route || path || url;

    if (!targetPath) {
      console.error('[BrowserControl] Navigate: route is required');
      this.sendAck('navigate', false, 'Route is required');
      return;
    }

    try {
      // Валидация: запрещаем любые URL (только локальные route)
      // Это критично для предотвращения прерывания голосового звонка!
      if (targetPath.includes('://') || targetPath.startsWith('//')) {
        console.error('[BrowserControl] Navigate: external URLs are FORBIDDEN! Use only client-side routes like "/cars" or "/cars/123". Full URLs will interrupt the voice call!');
        this.sendAck('navigate', false, 'External URLs are forbidden. Use only client-side routes starting with /');
        return;
      }

      // Валидация: route должен начинаться с "/" (абсолютный route в рамках приложения)
      if (!targetPath.startsWith('/')) {
        console.error('[BrowserControl] Navigate: route must start with "/" (e.g., "/cars", "/cars/123")');
        this.sendAck('navigate', false, 'Route must start with "/"');
        return;
      }

      // Формируем полный route с query параметрами
      let fullRoute = targetPath;

      if (queryParams) {
        const params = new URLSearchParams(queryParams);
        fullRoute += (targetPath.includes('?') ? '&' : '?') + params.toString();
      }

      // Навигация через HashRouter: устанавливаем hash
      // Это вызовет React Router БЕЗ перезагрузки страницы (критично для голосового звонка!)
      const newHash = `#${fullRoute}`;

      console.log('[BrowserControl] Client-side navigation to:', newHash);

      // Устанавливаем новый hash (React Router автоматически обработает)
      window.location.hash = newHash;

      this.sendAck('navigate', true, null, { route: fullRoute, hash: newHash });
    } catch (error) {
      console.error('[BrowserControl] Navigation error:', error);
      this.sendAck('navigate', false, error.message);
    }
  }

  /**
   * Клик по элементу
   */
  handleClick({ selector }) {
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.click();
        this.sendAck('click', true);
      } else {
        this.sendAck('click', false, `Element not found: ${selector}`);
      }
    }
  }

  /**
   * Заполнение поля
   */
  handleFill({ selector, value }) {
    if (selector && value !== undefined) {
      const element = document.querySelector(selector);
      if (element) {
        element.value = value;
        // Trigger change event
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
        this.sendAck('fill', true);
      } else {
        this.sendAck('fill', false, `Element not found: ${selector}`);
      }
    }
  }

  /**
   * Прокрутка страницы
   */
  handleScroll({ x = 0, y = 0 }) {
    window.scrollTo(x, y);
    this.sendAck('scroll', true);
  }

  /**
   * Выполнение параметризованной команды
   * Делегирует выполнение в commandExecutor
   * @param {Object} payload - Параметры команды
   * @param {string} payload.commandId - ID команды для выполнения
   * @param {Object} payload.params - Параметры команды (опционально)
   * @param {string} commandHash - Уникальный хеш команды для отслеживания
   */
  async handleExecute({ commandId, params } = {}, commandHash) {
    console.log('[BrowserControl] handleExecute called with:', { commandId, params, commandHash });

    if (!commandId) {
      console.error('[BrowserControl] Execute: commandId is required');
      this.sendAck('execute', false, 'commandId is required', null, commandHash);
      return;
    }

    console.log(`[BrowserControl] Executing parameterized command: ${commandId}`, params || {});

    // Issue #581-FIX: ACK a reload command BEFORE it runs. Its result would otherwise reach the
    // agent only from a page that is already unloading; the early ACK tells the agent at once
    // that the page reloads and which page it lands on (#1485).
    if (commandExecutor.willTriggerReload(commandId)) {
      // #1411: the early ACK below reports success before the command runs and its result is
      // dropped, so a call that cannot run must be refused FIRST — with an error the agent can
      // act on. Otherwise e.g. set_filters without `filters` was "done" while nothing navigated.
      const invalid = commandExecutor.validateReloadCommand(commandId, params || {});
      if (invalid) {
        console.warn(`[BrowserControl] ${commandId}: rejected before the early ACK — ${invalid}`);
        this.sendAck('execute', false, invalid, { commandId, params: params || {} }, commandHash);
        return;
      }

      console.log(`[BrowserControl][#581-FIX] ${commandId}: full-page reload — sending the early ACK`);
      const targetPage = commandExecutor.reloadTargetPage(commandId, params || {});
      this.sendAck('execute', true, null, {
        commandId,
        params: params || {},
        action: 'early_ack_navigation_pending',
        // #982/#1485: on willReload the agent waits for the target page's context and answers
        // in the same turn; b2b-api hands that answer to the reloaded page.
        willReload: true,
        ...(targetPage && { targetPage }),
      }, commandHash);

      // Navigate AFTER the ACK.
      try {
        await commandExecutor.execute(commandId, params);
      } catch (error) {
        console.error(`[BrowserControl] ${commandId} execute error (after ack):`, error);
      }
      return;
    }

    try {
      // Выполняем команду через централизованный executor (может быть async)
      const result = await commandExecutor.execute(commandId, params);

      if (result.success) {
        this.sendAck('execute', true, null, {
          commandId,
          params: params || {},
          action: result.action,
          warning: result.warning,
          results: result.results,  // Результаты фильтрации для агента
          // Issue #741: Enriched navigation response fields
          ...(result.context && { context: result.context }),
          ...(result.hint && { hint: result.hint }),
          ...(result.navigationHistory && { navigationHistory: result.navigationHistory }),
          ...(result.previousPage && { previousPage: result.previousPage }),
          // #1485: go_cars / go_back off the catalog fall back to a full reload; without these
          // the agent answered in the turn AND again after the reload (T86, H3).
          ...(result.willReload && { willReload: true }),
          ...(result.targetPage && { targetPage: result.targetPage }),
        }, commandHash);
      } else {
        this.sendAck('execute', false, result.error, {
          commandId,
          params: params || {}
        }, commandHash);
      }
    } catch (error) {
      console.error('[BrowserControl] Execute error:', error);
      this.sendAck('execute', false, error.message, {
        commandId,
        params: params || {}
      }, commandHash);
    }
  }

  /**
   * Отправка подтверждения выполнения команды
   * @param {string} command - Команда
   * @param {boolean} success - Успешность выполнения
   * @param {string} error - Ошибка (если есть)
   * @param {Object} data - Данные результата
   * @param {string} commandHash - Уникальный хеш команды
   */
  sendAck(command, success, error = null, data = null, commandHash = null) {
    const ackMessage = {
      type: 'ack',
      command,
      success,
      error,
      data,
      sessionId: this.sessionId,
      commandHash,
      timestamp: Date.now()
    };
    console.log('[BrowserControl] Sending ack:', ackMessage);
    this.send(ackMessage);
  }

  /**
   * Отправка сообщения на сервер
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  /**
   * Регистрация обработчика для определенного типа сообщений
   */
  onMessage(type, handler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type).push(handler);
  }

  /**
   * Удаление обработчика
   */
  offMessage(type, handler) {
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Issue #724: Ожидание ACK от бэкенда для context event.
   * Resolve при timeout (не reject) — fallback на текущее поведение.
   * @param {string} contextType - 'registered' | 'page-context' | 'update-actions-map'
   * @param {string|null} contextId - уникальный ID для matching (null = любой)
   * @param {number} timeout - макс. время ожидания в мс (по умолчанию 5000)
   * @returns {Promise<object>} ACK данные или { timeout: true }
   */
  waitForContextAck(contextType, contextId = null, timeout = 5000) {
    return new Promise((resolve) => {
      const listenType = contextType === 'registered' ? 'registered' : 'context-ack';

      const timer = setTimeout(() => {
        // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
        this.offMessage(listenType, handler);
        console.warn(`[BrowserControl][#724] ACK timeout for ${contextType} (${timeout}ms), proceeding anyway`);
        resolve({ timeout: true, contextType, contextId });
      }, timeout);

      const handler = (data) => {
        // Для 'registered' — любой registered message подходит
        // Для context-ack — проверяем contextType и contextId
        if (contextType === 'registered' ||
            (data.contextType === contextType && (!contextId || data.contextId === contextId))) {
          clearTimeout(timer);
          this.offMessage(listenType, handler);
          console.log(`[BrowserControl][#724] ACK received for ${contextType}`, data);
          resolve(data);
        }
      };

      this.onMessage(listenType, handler);
    });
  }

  /**
   * Issue #724: Отправить page context и дождаться ACK от бэкенда.
   * @param {Object} context - контекст страницы
   * @param {number} timeout - макс. время ожидания ACK
   * @returns {Promise<object>} результат { success, ack }
   */
  async sendPageContextWithAck(context, timeout = 5000) {
    const contextId = this.sendPageContext(context);
    if (!contextId) return { success: false, error: 'not connected' };

    const ack = await this.waitForContextAck('page-context', contextId, timeout);
    return { success: !ack.timeout, ack };
  }

  /**
   * Отключение от WebSocket
   */
  disconnect() {
    this.isIntentionallyClosed = true;
    this._connectPromise = null;

    if (this.ws) {
      // Отправляем сообщение о завершении сессии
      this.send({
        type: 'unregister',
        sessionId: this.sessionId,
        timestamp: Date.now()
      });

      this.ws.close();
      this.ws = null;
    }

    this.sessionId = null;
    this.messageHandlers.clear();
  }

  /**
   * Проверка состояния подключения
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * #1485: which socket is current — 1 for the first one opened, +1 per reconnect.
   * @returns {number}
   */
  getSocketGeneration() {
    return this.socketGeneration;
  }

  /**
   * Получение session ID
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Сохранение данных подключения в localStorage
   * ВАЖНО: SessionId НЕ сохраняется здесь, он управляется sessionManager
   */
  saveToLocalStorage() {
    try {
      // Сохраняем только server URL, sessionId управляется sessionManager
      if (this.serverUrl) {
        localStorage.setItem(this.STORAGE_KEY_SERVER_URL, this.serverUrl);
      }
    } catch (error) {
      console.error('[BrowserControl] Failed to save to localStorage:', error);
    }
  }

  /**
   * Восстановление данных подключения из localStorage
   * SessionId берётся из sessionManager, здесь только serverUrl
   */
  restoreFromLocalStorage() {
    try {
      const serverUrl = localStorage.getItem(this.STORAGE_KEY_SERVER_URL);

      if (serverUrl) {
        return { serverUrl };
      }
      return null;
    } catch (error) {
      console.error('[BrowserControl] Failed to restore from localStorage:', error);
      return null;
    }
  }

  /**
   * Очистка данных подключения из localStorage
   * ВАЖНО: НЕ очищает sessionId, он управляется sessionManager
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY_SERVER_URL);
    } catch (error) {
      console.error('[BrowserControl] Failed to clear localStorage:', error);
    }
  }

  /**
   * Автоматическое переподключение при загрузке страницы
   * Использует sessionId из sessionManager (с TTL 24 часа)
   *
   * @param {string} sessionId - SessionId из sessionManager
   * @returns {Promise<boolean>} true если успешно переподключено
   */
  async autoReconnect(sessionId) {
    const savedData = this.restoreFromLocalStorage();

    if (savedData && sessionId) {
      const { serverUrl } = savedData;
      console.log('[BrowserControl] Auto-reconnecting with session:', sessionId);

      try {
        await this.connect(serverUrl, sessionId);
        console.log('[BrowserControl] Auto-reconnect successful');
        return true;
      } catch (error) {
        console.error('[BrowserControl] Auto-reconnect failed:', error);
        this.clearLocalStorage();
        return false;
      }
    }

    console.log('[BrowserControl] No saved connection data or sessionId for auto-reconnect');
    return false;
  }

  /**
   * Установка конфигурации приложения
   * @param {Object} config - Конфигурация с routes и metadata
   */
  setAppConfig(config) {
    this.appConfig = {
      routes: config.routes || [],
      metadata: config.metadata || {}
    };

    console.log('[BrowserControl] App config updated:', this.appConfig);
  }

  /**
   * Получение информации о текущей странице
   * ВАЖНО: Для HashRouter используем hash вместо pathname
   * @returns {Object} Информация о странице
   */
  getCurrentPageInfo() {
    // Для HashRouter текущий route находится в hash (например: #/cars)
    const hash = window.location.hash;
    // Убираем символ # из начала, если он есть
    const route = hash.startsWith('#') ? hash.substring(1) : hash || '/';

    return {
      url: window.location.href,
      route: route,  // Для HashRouter: #/cars -> /cars
      hash: hash,    // Полный hash с символом #
      title: document.title,
      websitePage: resolveWebsitePage(),
      description: document.querySelector('meta[name="description"]')?.content || '',
      inventoryId: this.appConfig.metadata?.inventoryId  // ID источника инвентаря для voice agent
    };
  }

  /**
   * Генерация полной карты доступных действий
   * @returns {Object} Полная карта AvailableActionsMap
   */
  getActionsMap() {
    const currentPageInfo = this.getCurrentPageInfo();

    // Получаем информацию об автомобиле для detail страницы
    let currentCar = null;
    if (currentPageInfo.route.startsWith('/cars/')) {
      const carMatch = currentPageInfo.route.match(/^\/cars\/([^/]+)$/);
      if (carMatch) {
        const carId = carMatch[1];
        const allCars = typeof window !== 'undefined' ? window.allCars : null;
        if (Array.isArray(allCars)) {
          const car = allCars.find(c => c.id === carId);
          if (car) {
            currentCar = {
              id: car.id,
              title: carTitle(car),
              make: car.make,
              model: car.model,
              year: car.year,
              price: car.price,
              mileage: car.mileage,
              bodyStyle: car.bodyStyle || null,
              fuelType: car.fuelType || null,
              transmission: car.transmission || null,
              color: car.color || null,
              isHotOffer: car.isHotOffer || false
            };
          }
        }
      }
    }

    const actionsMap = {
      timestamp: Date.now(),
      routes: this.appConfig.routes,
      currentPage: currentPageInfo,
      ...(currentCar && { currentCar }), // Добавить currentCar если данные найдены
      commands: this.commands || [], // Параметризованные команды {id, params} (генерируются динамически)
      metadata: this.appConfig.metadata
    };

    return actionsMap;
  }

  /**
   * Обновление карты действий на сервере
   * Отправляет актуальную карту через WebSocket
   */
  updateActionsMap() {
    if (!this.isConnected()) {
      console.warn('[BrowserControl] Cannot update actions map - not connected');
      return null;
    }

    const actionsMap = this.getActionsMap();

    // Issue #724: Генерируем contextId для matching ACK
    const contextId = `ctx-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

    this.send({
      type: 'update-actions-map',
      sessionId: this.sessionId,
      actionsMap,
      contextId,
      timestamp: Date.now()
    });

    console.log('[BrowserControl] Actions map updated on server');
    return contextId;
  }

  /**
   * Обновление списка параметризованных команд
   * @param {Array} commands - Массив команд с полями {id, params}
   */
  updateCommands(commands) {
    this.commands = commands;
    console.log(`[BrowserControl] Commands updated: ${commands.length} parameterized commands available`);

    // Если подключены к серверу, отправляем обновленную карту действий
    if (this.isConnected()) {
      this.updateActionsMap();
    }
  }

  /**
   * Получить список доступных команд
   * @returns {Array} Массив команд
   */
  getCommands() {
    return this.commands || [];
  }

  /**
   * Вывести список команд в консоль в читаемом виде
   */
  listCommands() {
    const commands = this.getCommands();
    console.log(`\n=== Available Parameterized Commands (${commands.length}) ===\n`);
    commands.forEach((cmd, index) => {
      console.log(`${index + 1}. ${cmd.id}`);
      if (cmd.params && Object.keys(cmd.params).length > 0) {
        console.log(`   Params: ${JSON.stringify(cmd.params, null, 2)}\n`);
      } else {
        console.log(`   (No parameters)\n`);
      }
    });
    console.log('Use window.browserControl.execute("command_id", params) to execute a command\n');
  }

  /**
   * Выполнить команду по ID (для тестирования из консоли)
   * @param {string} commandId - ID команды
   * @param {Object} params - Параметры команды (опционально)
   * @returns {boolean} true если команда найдена и выполнена
   */
  executeCommand(commandId, params) {
    const commands = this.getCommands();
    const command = commands.find(cmd => cmd.id === commandId);

    if (!command) {
      console.error(`[BrowserControl] Command not found: ${commandId}`);
      console.log('Available commands:');
      commands.forEach(cmd => console.log(`  - ${cmd.id}`));
      return false;
    }

    console.log(`[BrowserControl] Executing command: ${commandId}`, params || {});

    // Выполняем команду локально
    this.handleExecute({ commandId, params: params || {} });

    return true;
  }

  /**
   * Поиск команд по ID
   * @param {string} query - Поисковый запрос
   * @returns {Array} Найденные команды
   */
  searchCommands(query) {
    const commands = this.getCommands();
    const lowerQuery = query.toLowerCase();

    const results = commands.filter(cmd =>
      cmd.id.toLowerCase().includes(lowerQuery)
    );

    console.log(`\n=== Search Results for "${query}" (${results.length}) ===\n`);
    results.forEach((cmd, index) => {
      console.log(`${index + 1}. ${cmd.id}`);
      if (cmd.params && Object.keys(cmd.params).length > 0) {
        console.log(`   Params: ${JSON.stringify(cmd.params, null, 2)}\n`);
      } else {
        console.log(`   (No parameters)\n`);
      }
    });

    return results;
  }

  /**
   * Отправить полное состояние страницы агенту
   * @param {Object} context - Контекст страницы из getCurrentPageContext()
   * @returns {boolean} true если сообщение отправлено успешно
   */
  sendPageContext(context) {
    if (!this.isConnected()) {
      console.warn('[BrowserControl] Cannot send page context: not connected');
      return null;
    }

    // Issue #724: Генерируем contextId для matching ACK
    const contextId = `ctx-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

    console.log('[BrowserControl] Sending page context:', { contextId, route: context?.route });

    this.send({
      type: 'page-context',
      sessionId: this.sessionId,
      context,
      contextId,
      timestamp: Date.now(),
    });

    return contextId;
  }

  /**
   * Отправить событие действия пользователя
   * @param {string} action - Тип действия ('navigation', 'filter-changed', 'car-view', 'modal-opened', etc.)
   * @param {Object} data - Данные действия
   * @param {boolean} includePageContext - Включать ли полный page context (по умолчанию true)
   * @param {string} source - Источник действия: 'user' (пользователь) или 'agent' (агент) (по умолчанию 'user')
   * @returns {boolean} true если сообщение отправлено успешно
   */
  sendUserAction(action, data, includePageContext = true, source = 'user') {
    if (!this.isConnected()) {
      return false;
    }

    const message = {
      type: 'user-action',
      sessionId: this.sessionId,
      // #1116: stamp the persistent visitor id so unique-people analytics
      // don't rely on a fragile session_id -> page_view.visitorId join
      // (a form submitter needn't have an agent session at all).
      visitorId: getOrCreateVisitorId(),
      action,
      data,
      source,
      timestamp: Date.now(),
    };

    if (includePageContext) {
      message.pageContext = getCurrentPageContext();
    }

    console.log(`[BrowserControl] ${source === 'agent' ? 'Agent' : 'User'} action: ${action}`, data);

    return this.send(message);
  }

  /**
   * Отправить событие действия агента (вспомогательный метод)
   * @param {string} action - Тип действия ('navigation', 'filter-changed', 'car-view', etc.)
   * @param {Object} data - Данные действия
   * @param {boolean} includePageContext - Включать ли полный page context (по умолчанию false)
   * @returns {boolean} true если сообщение отправлено успешно
   */
  sendAgentAction(action, data, includePageContext = false) {
    return this.sendUserAction(action, data, includePageContext, 'agent');
  }

  /**
   * Отправить текстовое сообщение для AI агента (text chat mode)
   * Используется когда WebRTC соединение не установлено
   * @param {string} userMessage - Текст сообщения пользователя (может быть пустым при launchContext)
   * @param {Object} options - Дополнительные опции
   * @param {string} options.workflowType - Тип workflow (MAIN_WORKFLOW, AUTOHEAVEN_WORKFLOW, ANDYS_WORKFLOW)
   * @param {string} options.inventoryId - ID инвентаря (опционально)
   * @param {string} options.messageId - Уникальный ID сообщения для tracking
   * @param {Object} options.pageContext - Issue #409: Контекст страницы (route, url, activeFilters, etc.)
   * @param {Object} options.launchContext - Issue #461: Контекст запуска для auto-greeting (action, metadata)
   * @returns {string|null} messageId если сообщение отправлено, null если ошибка
   */
  sendChatMessage(userMessage, options = {}) {
    if (!this.isConnected()) {
      console.warn('[BrowserControl] Cannot send chat message - not connected');
      return null;
    }

    // Issue #461: Разрешаем пустые сообщения если есть launchContext (для auto-greeting)
    const hasLaunchContext = options.launchContext && typeof options.launchContext === 'object';
    if ((!userMessage || userMessage.trim().length === 0) && !hasLaunchContext) {
      console.warn('[BrowserControl] Cannot send empty chat message without launchContext');
      return null;
    }

    // Генерируем уникальный messageId для tracking
    const messageId = options.messageId || `msg-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    const message = {
      type: 'chat-message',
      sessionId: this.sessionId,
      userMessage: userMessage ? userMessage.trim() : '', // Issue #461: Может быть пустым
      workflowType: options.workflowType || 'MAIN_WORKFLOW',
      inventoryId: options.inventoryId,
      messageId,
      pageContext: options.pageContext, // Issue #409: Передаём контекст страницы для агента
      launchContext: options.launchContext, // Issue #461: Контекст запуска для auto-greeting
      suggestions: options.suggestions, // Issue #886: nested cfg {enabled, params:{max_suggestions}}
      // #1536: the persistent visitor id — the agent's Langfuse user id for this turn.
      visitorId: getOrCreateVisitorId(),
      timestamp: Date.now(),
    };

    // Issue #587: Diagnostic log for duplicate request investigation
    const _launchAction = options.launchContext?.action || null;
    console.warn('[BrowserControl][#587-DIAG] sendChatMessage WS SEND:', {
      messageId,
      length: userMessage ? userMessage.length : 0,
      hasLaunchContext,
      launchAction: _launchAction,
      sessionId: this.sessionId,
      timestamp: Date.now(),
    });

    this.send(message);
    return messageId;
  }

  /**
   * Запросить историю сообщений из checkpoints для текущей сессии
   * Ответ приходит через message handler с type='chat-history'
   * @returns {boolean} true если запрос отправлен успешно
   */
  getChatHistory() {
    if (!this.isConnected()) {
      console.warn('[BrowserControl] Cannot get chat history - not connected');
      return false;
    }

    if (!this.sessionId) {
      console.warn('[BrowserControl] Cannot get chat history - no sessionId');
      return false;
    }

    console.log('[BrowserControl] Requesting chat history for session:', this.sessionId);

    this.send({
      type: 'get-chat-history',
      sessionId: this.sessionId,
      timestamp: Date.now(),
    });

    return true;
  }

  /**
   * Отправить взаимодействие пользователя с виджетом
   * @param {string} widgetId - ID виджета
   * @param {string} action - Действие ('submit_form', 'button_click', 'select_option', etc.)
   * @param {Object} data - Данные взаимодействия (значения формы, выбранные опции, etc.)
   * @returns {boolean} true если сообщение отправлено успешно
   */
  sendWidgetInteraction(widgetId, action, data) {
    if (!this.isConnected()) {
      console.warn('[BrowserControl] Cannot send widget interaction - not connected');
      return false;
    }

    const message = {
      type: 'widget_interaction',
      sessionId: this.sessionId,
      widgetId,
      action,
      data,
      timestamp: Date.now(),
    };

    console.log(`[BrowserControl] Widget interaction: ${action} (widget: ${widgetId})`, data);

    return this.send(message);
  }
}

// Singleton экземпляр
export const browserControlWS = new BrowserControlWebSocket();

// Добавляем глобальные методы для удобного тестирования из консоли браузера
if (typeof window !== 'undefined') {
  window.browserControl = {
    /**
     * Выполнить параметризованную команду по ID
     * @example window.browserControl.execute('view_cars', { offerId: '534162-1' })
     * @example window.browserControl.execute('set_filter', { filterType: 'make', values: ['BMW'] })
     * @example window.browserControl.execute('go_home')
     */
    execute: (commandId, params) => browserControlWS.executeCommand(commandId, params),

    /**
     * Получить список всех доступных параметризованных команд
     * @example window.browserControl.getCommands()
     */
    getCommands: () => browserControlWS.getCommands(),

    /**
     * Показать список параметризованных команд в консоли
     * @example window.browserControl.list()
     */
    list: () => browserControlWS.listCommands(),

    /**
     * Поиск команд по ID
     * @example window.browserControl.search('filter')
     */
    search: (query) => browserControlWS.searchCommands(query),

    /**
     * Получить информацию о подключении
     * @example window.browserControl.status()
     */
    status: () => {
      const connected = browserControlWS.isConnected();
      const sessionId = browserControlWS.getSessionId();
      const commandsCount = browserControlWS.getCommands().length;
      const currentPage = browserControlWS.getCurrentPageInfo();

      console.log('\n=== Browser Control Status ===');
      console.log(`Connected: ${connected ? '✓' : '✗'}`);
      console.log(`Session ID: ${sessionId || 'N/A'}`);
      console.log(`Commands available: ${commandsCount}`);
      console.log(`Current route: ${currentPage.route}`);
      console.log(`Current URL: ${currentPage.url}\n`);

      return { connected, sessionId, commandsCount, currentPage };
    },

    /**
     * Справка по использованию
     * @example window.browserControl.help()
     */
    help: () => {
      console.log('\n=== Browser Control Help ===\n');
      console.log('Available methods:');
      console.log('  window.browserControl.list()                         - Show all available parameterized commands');
      console.log('  window.browserControl.search("query")                - Search commands by ID');
      console.log('  window.browserControl.execute("cmd_id", params)      - Execute a parameterized command');
      console.log('  window.browserControl.getCommands()                  - Get commands array');
      console.log('  window.browserControl.status()                       - Show connection status');
      console.log('  window.browserControl.debugVoiceSession()            - Start debug session for browser control testing');
      console.log('  window.browserControl.help()                         - Show this help\n');
      console.log('Examples:');
      console.log('  window.browserControl.list()');
      console.log('  window.browserControl.search("filter")');
      console.log('  window.browserControl.execute("go_home")');
      console.log('  window.browserControl.execute("set_filter", { filterType: "make", values: ["BMW"] })');
      console.log('  window.browserControl.execute("view_cars", { offerId: "534162-1" })');
      console.log('  window.browserControl.execute("set_filters", { filters: { make: ["BMW"], price: { max: 50000 } } })');
      console.log('  window.browserControl.debugVoiceSession()');
      console.log('');
    },

    /**
     * Запуск отладочной сессии управления браузером БЕЗ подключения к голосовому ассистенту
     * Полезно для тестирования команд управления браузером
     * @example window.browserControl.debugVoiceSession()
     */
    debugVoiceSession: async () => {
      try {
        // Импортируем sessionManager
        const { getOrCreateSessionId, refreshSession } = await import('./sessionManager.js');

        // Получаем или создаём sessionId
        const sessionId = getOrCreateSessionId();
        console.log('\n=== Debug Voice Session ===');
        console.log(`Session ID: ${sessionId}`);

        // Обновляем timestamp сессии
        refreshSession();

        // Получаем WebSocket URL из конфигурации
        const wsUrl = config.BROWSER_CONTROL_WS_URL;
        console.log(`WebSocket URL: ${wsUrl}`);

        // Подключаемся к WebSocket серверу
        console.log('\nConnecting to Browser Control WebSocket...');
        await browserControlWS.connect(wsUrl, sessionId);

        console.log('\n✓ Debug session started successfully!');
        console.log('\nNow you can:');
        console.log('  - Use window.browserControl.list() to see available commands');
        console.log('  - Use window.browserControl.execute("command_id") to execute commands');
        console.log('  - Use window.browserControl.status() to check connection status');
        console.log('  - Send commands from external systems using sessionId:', sessionId);
        console.log('\nTo disconnect, use: browserControlWS.disconnect()\n');

        return { success: true, sessionId, wsUrl };
      } catch (error) {
        console.error('\n✗ Failed to start debug session:', error);
        console.error('Error details:', error.message);
        return { success: false, error: error.message };
      }
    }
  };

  console.log('[BrowserControl] Global commands available via window.browserControl');
  console.log('Type window.browserControl.help() for usage information');
}

export default BrowserControlWebSocket;
